import { Circle, G, Line, Path, Polygon, Rect, Svg, Text } from '@react-pdf/renderer'
import { LayoutObjectDto, RouteStepResponse, YardBlockDto } from '@yard/app/api'
import { blockPixels, rectPolyPoints } from '@yard/components/MiniMap/geometry'
import { RoadNodeDto, RoadSegmentDto } from '@yard/features/road-network/models/road-network.model'
import React from 'react'
import type { Stop } from './resolve-stops'
export type { Stop } from './resolve-stops'

// @react-pdf/renderer's Text type doesn't expose SVG positional props when used
// inside <Svg>, even though they work at runtime. This cast adds them back.
const SvgText = Text as React.ComponentType<{
  x?: number
  y?: number
  fontSize?: number
  fill?: string
  fontWeight?: 'bold' | 'normal'
  textAnchor?: 'start' | 'middle' | 'end'
  children?: React.ReactNode
}>

// ── i18n labels ────────────────────────────────────────────────────────────
// react-pdf renders in its own reconciler — Tolgee context is not available
// inside it. Callers resolve labels via useTranslate() and pass them as props.
export interface RoutingSlipMiniMapLabels {
  startEnd: string
  youAreHere: string
  entryRoute: string
  exitRoute: string
  otherRoads: string
  block: string
}

const DEFAULT_LABELS: RoutingSlipMiniMapLabels = {
  startEnd: 'start / end',
  youAreHere: 'you are here',
  entryRoute: 'Entry route',
  exitRoute: 'Exit route',
  otherRoads: 'Other roads',
  block: 'Block',
}

export interface RoutingSlipMiniMapProps {
  entrySteps?: RouteStepResponse[]
  exitSteps?: RouteStepResponse[]
  allYardBlocks: YardBlockDto[]
  stopHandoverAreas: LayoutObjectDto[]
  roadSegments?: RoadSegmentDto[]
  stops: Stop[]
  roadNodes: RoadNodeDto[]
  gate: LayoutObjectDto | null
  primaryColor: string
  secondaryColor: string
  labels?: Partial<RoutingSlipMiniMapLabels>
}

// ── Layout ─────────────────────────────────────────────────────────────────
const SVG_WIDTH = 515
const SVG_HEIGHT = 280
const MAP_WIDTH = 340
const MAP_HEIGHT = SVG_HEIGHT
const MAP_CX = MAP_WIDTH / 2
const MAP_CY = MAP_HEIGHT / 2
const LEGEND_WIDTH = 120
const LEGEND_X = SVG_WIDTH - LEGEND_WIDTH
const LEGEND_PAD = 6
const LEGEND_ROW = 18
// All legend icons fit in a 20px slot; text starts 4px after the slot
const LEGEND_ICON_W = 20
const LEGEND_TEXT_OFFSET = 24
const GATE_RADIUS = 7
const LEGEND_FONT_SIZE = 8
const LEGEND_TEXT_COLOR = '#444444'
const LEGEND_SUBTEXT_SIZE = 7
const LEGEND_SUBTEXT_COLOR = '#888888'
const MIN_BBOX_SIZE = 100
const STOP_FILL = '#e8eef8'
const NON_STOP_BLOCK_FILL = '#f5f5f5'

// ── Map pin ────────────────────────────────────────────────────────────────
const PIN_R = 7
const PIN_TAIL = 5
const LEG_PIN_R = 5
const LEG_PIN_TAIL = 4

// ── Route visuals ──────────────────────────────────────────────────────────
const ROUTE_WIDTH = 2
const EXIT_DASH = '9.5'
// Exit route is offset this many px perpendicular-right on shared segments
const LANE_OFFSET = 3
// Pins sharing a screen position are tilted around their shared tip, spread
// symmetrically across this total arc (degrees) so every numbered head is
// visible like two pushpins leaning apart.
const COINCIDENT_PIN_SPREAD_DEG = 80
// Tips of coincident pins are also shifted horizontally (along the lean
// direction) by this many px, so the tips sit side-by-side rather than all
// stacking on the same pixel.
const COINCIDENT_TIP_GAP_PX = 2
const BG_ROAD_COLOR = '#cccccc'
const BG_ROAD_WIDTH = 1
const BBOX_PAD_FACTOR = 0.06

// ── Helpers ────────────────────────────────────────────────────────────────
/**
 * Classic teardrop map-pin SVG path. Tip at (cx, cy), head above.
 *
 *   ╭───╮
 *   │ n │  ← circular head
 *   ╰─┬─╯
 *     ▼    ← tip at (cx, cy)
 */
const mapPinPath = (cx: number, cy: number, r = PIN_R, tail = PIN_TAIL, rotDeg = 0): string => {
  const rad = (rotDeg * Math.PI) / 180
  const cos = Math.cos(rad),
    sin = Math.sin(rad)
  // Rotate (dx, dy) around the tip at (0, 0), then translate to (cx, cy).
  const rot = (dx: number, dy: number) => ({
    x: cx + cos * dx - sin * dy,
    y: cy + sin * dx + cos * dy,
  })
  const cp1x = r * 0.55
  const cp1y = tail * 0.35
  const headRelY = -tail - r
  const p1 = rot(-cp1x, -cp1y)
  const p2 = rot(-r, headRelY + r * 0.85)
  const p3 = rot(-r, headRelY)
  const p4 = rot(r, headRelY)
  const p5 = rot(r, headRelY + r * 0.85)
  const p6 = rot(cp1x, -cp1y)
  return [
    `M ${cx},${cy}`,
    `C ${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y}`,
    `A ${r},${r},0,1,1,${p4.x},${p4.y}`,
    `C ${p5.x},${p5.y} ${p6.x},${p6.y} ${cx},${cy}`,
    'Z',
  ].join(' ')
}

// ── Component ──────────────────────────────────────────────────────────────
export const RoutingSlipMiniMap: React.FC<RoutingSlipMiniMapProps> = ({
  entrySteps = [],
  exitSteps = [],
  allYardBlocks,
  stopHandoverAreas,
  roadSegments = [],
  stops,
  roadNodes,
  gate,
  primaryColor,
  secondaryColor,
  labels,
}) => {
  const l = { ...DEFAULT_LABELS, ...labels }

  const nodeMap = new Map<string, { x: number; y: number }>(
    roadNodes.map(n => [n.id, { x: n.layoutPosition.x, y: n.layoutPosition.y }]),
  )

  const entryBySegId = new Map<string, RouteStepResponse>(entrySteps.map(s => [s.segmentId, s]))
  const exitBySegId = new Map<string, RouteStepResponse>(exitSteps.map(s => [s.segmentId, s]))
  const allRouteSegIds = new Set([...entryBySegId.keys(), ...exitBySegId.keys()])
  const sharedSegIds = new Set([...entryBySegId.keys()].filter(id => exitBySegId.has(id)))

  // Collect unique route nodes for bounding box
  const visitedNodes = new Set<string>()
  const waypointSet: { x: number; y: number }[] = []
  for (const step of [...entrySteps, ...exitSteps]) {
    for (const nodeId of [step.fromNodeId, step.toNodeId]) {
      if (!visitedNodes.has(nodeId)) {
        visitedNodes.add(nodeId)
        const p = nodeMap.get(nodeId)
        if (p) waypointSet.push(p)
      }
    }
  }

  const stopBlockIds = new Set(stops.filter(s => s.kind === 'block').map(s => s.id))
  const stopBlocks = allYardBlocks.filter(b => stopBlockIds.has(b.id))

  if (stops.length === 0 && waypointSet.length === 0 && !gate) return null

  // ── Bounding box ──────────────────────────────────────────────────────────
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity
  const expand = (x: number, y: number, w = 0, h = 0) => {
    minX = Math.min(minX, x)
    minY = Math.min(minY, y)
    maxX = Math.max(maxX, x + w)
    maxY = Math.max(maxY, y + h)
  }
  if (gate) expand(gate.layoutPosition.x, gate.layoutPosition.y, gate.width, gate.height)
  waypointSet.forEach(p => expand(p.x, p.y))
  stopBlocks.forEach(b => {
    const { w, h } = blockPixels(b)
    expand(b.layoutPosition.x, b.layoutPosition.y, w, h)
  })
  stopHandoverAreas.forEach(h => expand(h.layoutPosition.x, h.layoutPosition.y, h.width, h.height))
  if (minX === Infinity) return null

  const bboxW = Math.max(maxX - minX, MIN_BBOX_SIZE)
  const bboxH = Math.max(maxY - minY, MIN_BBOX_SIZE)
  const padX = bboxW * BBOX_PAD_FACTOR,
    padY = bboxH * BBOX_PAD_FACTOR
  const pMinX = minX - padX,
    pMinY = minY - padY
  const pW = bboxW + 2 * padX,
    pH = bboxH + 2 * padY

  // ── Rotation: first route segment runs straight up (like north on a map) ──
  let mapRotDeg = 0
  if (gate) {
    const gcx = gate.layoutPosition.x + gate.width / 2
    const gcy = gate.layoutPosition.y + gate.height / 2
    let northDx = 0,
      northDy = 0

    if (entrySteps.length > 0) {
      const firstNode = nodeMap.get(entrySteps[0].toNodeId)
      if (firstNode) {
        northDx = firstNode.x - gcx
        northDy = firstNode.y - gcy
      }
    }
    if (northDx === 0 && northDy === 0 && stops.length > 0) {
      northDx = stops.reduce((s, st) => s + st.position.x, 0) / stops.length - gcx
      northDy = stops.reduce((s, st) => s + st.position.y, 0) / stops.length - gcy
    }
    if (northDx === 0 && northDy === 0) {
      northDx = pMinX + pW / 2 - gcx
      northDy = pMinY + pH / 2 - gcy
    }

    if (northDx !== 0 || northDy !== 0) {
      let deg = (-Math.PI / 2 - Math.atan2(northDy, northDx)) * (180 / Math.PI)
      deg = ((deg % 360) + 360) % 360
      if (deg > 180) deg -= 360
      mapRotDeg = deg
    }
  }

  const rad = mapRotDeg * (Math.PI / 180)
  const cosRAbs = Math.abs(Math.cos(rad)),
    sinRAbs = Math.abs(Math.sin(rad))
  const scale = Math.min(
    MAP_WIDTH / (cosRAbs * pW + sinRAbs * pH),
    MAP_HEIGHT / (sinRAbs * pW + cosRAbs * pH),
  )
  const offX = (MAP_WIDTH - pW * scale) / 2
  const offY = (MAP_HEIGHT - pH * scale) / 2

  const xOf = (v: number) => (v - pMinX) * scale + offX
  const yOf = (v: number) => (v - pMinY) * scale + offY
  const cosR = Math.cos(rad),
    sinR = Math.sin(rad)
  const rotX = (sx: number, sy: number) => MAP_CX + cosR * (sx - MAP_CX) - sinR * (sy - MAP_CY)
  const rotY = (sx: number, sy: number) => MAP_CY + sinR * (sx - MAP_CX) + cosR * (sy - MAP_CY)
  const fx = (wx: number, wy: number) => rotX(xOf(wx), yOf(wy))
  const fy = (wx: number, wy: number) => rotY(xOf(wx), yOf(wy))

  const rectPoly = (
    x: number,
    y: number,
    w: number,
    h: number,
    rotationDeg: number | null | undefined,
  ): string => rectPolyPoints(x, y, w, h, rotationDeg, { fx, fy })

  // Pre-compute block layout (polygon + center) once — reused by shape and pin layers
  type BlockLayout = { polyPoints: string; cx: number; cy: number; isStop: boolean }
  const blockLayouts = new Map<string, BlockLayout>()
  for (const block of allYardBlocks) {
    const { w, h } = blockPixels(block)
    const bcx = block.layoutPosition.x + w / 2
    const bcy = block.layoutPosition.y + h / 2
    blockLayouts.set(block.id, {
      polyPoints: rectPoly(
        block.layoutPosition.x,
        block.layoutPosition.y,
        w,
        h,
        block.layoutPosition.rotation,
      ),
      cx: fx(bcx, bcy),
      cy: fy(bcx, bcy),
      isStop: stopBlockIds.has(block.id),
    })
  }

  const bgSegments = roadSegments.filter(s => !allRouteSegIds.has(s.id))

  const renderRouteSeg = (step: RouteStepResponse, isDashed: boolean, keyPrefix: string) => {
    const from = nodeMap.get(step.fromNodeId),
      to = nodeMap.get(step.toNodeId)
    if (!from || !to) return null

    let x1 = fx(from.x, from.y),
      y1 = fy(from.x, from.y)
    let x2 = fx(to.x, to.y),
      y2 = fy(to.x, to.y)

    if (isDashed && sharedSegIds.has(step.segmentId)) {
      const dx = x2 - x1,
        dy = y2 - y1
      const len = Math.sqrt(dx * dx + dy * dy)
      if (len > 0) {
        const ox = (-dy / len) * LANE_OFFSET
        const oy = (dx / len) * LANE_OFFSET
        x1 += ox
        y1 += oy
        x2 += ox
        y2 += oy
      }
    }

    const color = isDashed ? secondaryColor : primaryColor
    return (
      <Line
        key={`${keyPrefix}-${step.segmentId}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={color}
        strokeWidth={ROUTE_WIDTH}
        strokeLinecap='round'
        strokeDasharray={isDashed ? EXIT_DASH : undefined}
      />
    )
  }

  const legendLineSample = (lx: number, ly: number, isDashed: boolean) => {
    const color = isDashed ? secondaryColor : primaryColor
    return (
      <Line
        x1={lx}
        y1={ly}
        x2={lx + LEGEND_ICON_W}
        y2={ly}
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap='round'
        strokeDasharray={isDashed ? '4,2.5' : undefined}
      />
    )
  }

  const renderGateMarker = () => {
    if (!gate) return null
    const gcx = fx(gate.layoutPosition.x + gate.width / 2, gate.layoutPosition.y + gate.height / 2)
    const gcy = fy(gate.layoutPosition.x + gate.width / 2, gate.layoutPosition.y + gate.height / 2)
    return (
      <G>
        <Circle cx={gcx} cy={gcy} r={GATE_RADIUS} fill='black' />
        <SvgText x={gcx + GATE_RADIUS + 4} y={gcy + 3} fontSize={8} fill='black'>
          {gate.name}
        </SvgText>
        <SvgText x={gcx} y={gcy + GATE_RADIUS + 10} fontSize={7} fill='#666666' textAnchor='middle'>
          {l.youAreHere}
        </SvgText>
      </G>
    )
  }

  const renderLegendGateSection = () => {
    if (!gate) return null
    const ry = 28 + stops.length * LEGEND_ROW + 4
    const lx = LEGEND_X + LEGEND_PAD
    return (
      <G>
        <Rect x={LEGEND_X - 6} y={ry - 4} width={LEGEND_WIDTH} height={0.75} fill='#e0e0e0' />
        <Circle cx={lx + 10} cy={ry + 10} r={LEG_PIN_R} fill={LEGEND_TEXT_COLOR} />
        <SvgText
          x={lx + LEGEND_TEXT_OFFSET}
          y={ry + 14}
          fontSize={LEGEND_FONT_SIZE}
          fill={LEGEND_TEXT_COLOR}
        >
          {gate.name.length > 18 ? gate.name.slice(0, 17) + '…' : gate.name}
        </SvgText>
        <SvgText
          x={lx + LEGEND_TEXT_OFFSET}
          y={ry + 24}
          fontSize={LEGEND_SUBTEXT_SIZE}
          fill={LEGEND_SUBTEXT_COLOR}
        >
          {l.startEnd}
        </SvgText>
        <Rect x={lx} y={ry + 32} width={LEGEND_ICON_W} height={12} fill={STOP_FILL} />
        <SvgText
          x={lx + LEGEND_TEXT_OFFSET}
          y={ry + 41}
          fontSize={LEGEND_FONT_SIZE}
          fill={LEGEND_TEXT_COLOR}
        >
          {l.block}
        </SvgText>
      </G>
    )
  }

  const renderLegendRouteKey = () => {
    // 56 = height of the gate+block section (divider + gate rows + block row)
    const baseY = 28 + stops.length * LEGEND_ROW + (gate ? 56 : 4) + 14
    const lx = LEGEND_X + LEGEND_PAD
    return (
      <G>
        <Rect x={LEGEND_X - 6} y={baseY - 6} width={LEGEND_WIDTH} height={0.75} fill='#e0e0e0' />
        {legendLineSample(lx, baseY + 6, false)}
        <SvgText
          x={lx + LEGEND_TEXT_OFFSET}
          y={baseY + 10}
          fontSize={LEGEND_FONT_SIZE}
          fill={LEGEND_TEXT_COLOR}
        >
          {l.entryRoute}
        </SvgText>
        {legendLineSample(lx, baseY + 20, true)}
        <SvgText
          x={lx + LEGEND_TEXT_OFFSET}
          y={baseY + 24}
          fontSize={LEGEND_FONT_SIZE}
          fill={LEGEND_TEXT_COLOR}
        >
          {l.exitRoute}
        </SvgText>
        <Line
          x1={lx}
          y1={baseY + 34}
          x2={lx + LEGEND_ICON_W}
          y2={baseY + 34}
          stroke={BG_ROAD_COLOR}
          strokeWidth={1.5}
          strokeLinecap='round'
        />
        <SvgText
          x={lx + LEGEND_TEXT_OFFSET}
          y={baseY + 38}
          fontSize={LEGEND_FONT_SIZE}
          fill={LEGEND_TEXT_COLOR}
        >
          {l.otherRoads}
        </SvgText>
      </G>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Svg width={SVG_WIDTH} height={SVG_HEIGHT}>
      {/* Background roads */}
      {bgSegments.map(seg => {
        const from = nodeMap.get(seg.startNodeId),
          to = nodeMap.get(seg.endNodeId)
        if (!from || !to) return null
        return (
          <Line
            key={`bg-${seg.id}`}
            x1={fx(from.x, from.y)}
            y1={fy(from.x, from.y)}
            x2={fx(to.x, to.y)}
            y2={fy(to.x, to.y)}
            stroke={BG_ROAD_COLOR}
            strokeWidth={BG_ROAD_WIDTH}
            strokeLinecap='round'
          />
        )
      })}

      {/* Block shapes — drawn before routes so routes overlap them */}
      {allYardBlocks.map(block => {
        const layout = blockLayouts.get(block.id)
        if (!layout) return null
        return (
          <Polygon
            key={`shape-${block.id}`}
            points={layout.polyPoints}
            fill={layout.isStop ? STOP_FILL : NON_STOP_BLOCK_FILL}
          />
        )
      })}

      {/* Stop handover areas — drawn with stop fill, same layer as blocks */}
      {stopHandoverAreas.map(h => (
        <Polygon
          key={`handover-${h.id}`}
          points={rectPoly(
            h.layoutPosition.x,
            h.layoutPosition.y,
            h.width,
            h.height,
            h.layoutPosition.rotation,
          )}
          fill={STOP_FILL}
        />
      ))}

      {/* Entry route */}
      {[...entryBySegId.values()].map(step => renderRouteSeg(step, false, 'entry'))}

      {/* Exit route */}
      {[...exitBySegId.values()].map(step => renderRouteSeg(step, true, 'exit'))}

      {/* Stop map-pin markers — drawn last so they sit above everything.
          Pins sharing a tip position are rotated around the tip, fanning out
          like a bouquet of pushpins so every numbered head is visible. */}
      {(() => {
        const positions = stops.map(s => ({
          cx: fx(s.position.x, s.position.y),
          cy: fy(s.position.x, s.position.y),
        }))
        const keyOf = (p: { cx: number; cy: number }) => `${Math.round(p.cx)},${Math.round(p.cy)}`
        const groupSize = new Map<string, number>()
        positions.forEach(p => {
          const k = keyOf(p)
          groupSize.set(k, (groupSize.get(k) ?? 0) + 1)
        })
        const groupIdx = new Map<string, number>()
        const angles = positions.map(p => {
          const k = keyOf(p)
          const size = groupSize.get(k) ?? 1
          if (size <= 1) return 0
          const idx = groupIdx.get(k) ?? 0
          groupIdx.set(k, idx + 1)
          const half = COINCIDENT_PIN_SPREAD_DEG / 2
          return -half + (COINCIDENT_PIN_SPREAD_DEG * idx) / (size - 1)
        })
        return stops.map((stop, i) => {
          const { cx, cy } = positions[i]
          const angle = angles[i]
          const rad = (angle * Math.PI) / 180
          const tipCx = cx + Math.sin(rad) * COINCIDENT_TIP_GAP_PX
          const tipCy = cy
          const headCx = tipCx + Math.sin(rad) * (PIN_TAIL + PIN_R)
          const headCy = tipCy - Math.cos(rad) * (PIN_TAIL + PIN_R)
          return (
            <G key={`stop-${i}-${stop.id}`}>
              <Path d={mapPinPath(tipCx, tipCy, PIN_R, PIN_TAIL, angle)} fill='black' />
              <SvgText
                x={headCx}
                y={headCy + 3}
                fontSize={7}
                fill='white'
                textAnchor='middle'
                fontWeight='bold'
              >
                {String(i + 1)}
              </SvgText>
            </G>
          )
        })
      })()}

      {/* Gate marker */}
      {renderGateMarker()}

      {/* Legend — white background prevents map roads bleeding through */}
      <Rect x={LEGEND_X} y={0} width={LEGEND_WIDTH} height={SVG_HEIGHT} fill='white' />

      {stops.map((stop, i) => {
        const ry = 28 + i * LEGEND_ROW
        const lx = LEGEND_X + LEGEND_PAD
        const pinX = lx + 10
        const tipY = ry + LEG_PIN_TAIL
        const headCy = tipY - LEG_PIN_TAIL - LEG_PIN_R
        return (
          <G key={`leg-${i}-${stop.id}`}>
            <Path d={mapPinPath(pinX, tipY, LEG_PIN_R, LEG_PIN_TAIL)} fill='black' />
            <SvgText
              x={pinX}
              y={headCy + 3}
              fontSize={6}
              fill='white'
              textAnchor='middle'
              fontWeight='bold'
            >
              {String(i + 1)}
            </SvgText>
            <SvgText x={lx + LEGEND_TEXT_OFFSET} y={ry + 3} fontSize={8} fill='#222222'>
              {stop.name.length > 18 ? stop.name.slice(0, 17) + '…' : stop.name}
            </SvgText>
          </G>
        )
      })}

      {renderLegendGateSection()}
      {renderLegendRouteKey()}
    </Svg>
  )
}
